import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import type { EirsaliyeRow } from '@/lib/eirsaliye-service';

interface EirsaliyeTableProps {
  data: EirsaliyeRow[];
  count: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

function statusBadge(row: Pick<EirsaliyeRow, 'status' | 'all_items_stock_imported'>) {
  if (row.all_items_stock_imported && row.status === 'active') {
    return (
      <Badge
        variant="secondary"
        className="text-xs bg-emerald-600 text-white hover:bg-emerald-600 border-transparent"
      >
        Kapatildi
      </Badge>
    );
  }
  switch (row.status) {
    case 'active':
      return <Badge variant="default" className="text-xs">Aktif</Badge>;
    case 'needs_review':
      return <Badge variant="destructive" className="text-xs">Inceleme</Badge>;
    case 'corrected':
      return <Badge variant="secondary" className="text-xs">Duzeltildi</Badge>;
    case 'ignored':
      return <Badge variant="outline" className="text-xs">Yok Sayildi</Badge>;
    default:
      return <Badge variant="outline" className="text-xs">{row.status}</Badge>;
  }
}

function parsedByBadge(parsedBy: string) {
  if (parsedBy === 'ai_fallback') {
    return <Badge variant="secondary" className="text-[10px]">AI</Badge>;
  }
  return null;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return dateStr;
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `₺${value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function EirsaliyeTable({ data, count, page, pageSize, isLoading, onPageChange }: EirsaliyeTableProps) {
  const navigate = useNavigate();
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const rangeFrom = count === 0 ? 0 : page * pageSize + 1;
  const rangeTo = count === 0 ? 0 : Math.min((page + 1) * pageSize, count);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5" />
            Irsaliyeler
          </CardTitle>
          <span className="text-sm text-muted-foreground">{count} kayit</span>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10 text-muted-foreground">Yukleniyor...</div>
        ) : data.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Henuz irsaliye bulunamadi. Senkronizasyon baslatin.</p>
          </div>
        ) : (
          <>
            {/* No fixed max-height: avoids cutting rows in half; pagination limits rows to pageSize. */}
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full min-w-[720px] text-sm border-collapse">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs">
                    <th className="text-left py-2 pr-3 align-middle">Irsaliye No</th>
                    <th className="text-left py-2 pr-3 align-middle whitespace-nowrap">Tarih</th>
                    <th className="text-left py-2 pr-3 align-middle">ERP No</th>
                    <th className="text-left py-2 pr-3 align-middle">Bayi Kodu</th>
                    <th className="text-left py-2 pr-3 align-middle max-w-[160px]">Sube</th>
                    <th className="text-right py-2 pr-3 align-middle whitespace-nowrap">Adet</th>
                    <th className="text-right py-2 pr-3 align-middle whitespace-nowrap">Toplam Maliyet</th>
                    <th className="text-center py-2 pr-3 align-middle whitespace-nowrap">Durum</th>
                    <th className="text-center py-2 align-middle whitespace-nowrap">Metod</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-border/60 last:border-0 hover:bg-muted/40 transition-colors cursor-pointer"
                      onClick={() => navigate(`/eirsaliye/${row.id}`)}
                    >
                      <td className="py-2.5 pr-3 align-middle font-mono text-xs font-medium whitespace-nowrap">
                        {row.irsaliye_no}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-xs whitespace-nowrap">
                        {formatDate(row.irsaliye_tarihi)}
                      </td>
                      <td className="py-2.5 pr-3 align-middle font-mono text-xs whitespace-nowrap">
                        {row.erp_irsaliye_no || '-'}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-xs whitespace-nowrap">{row.bayi_kodu || '-'}</td>
                      <td className="py-2.5 pr-3 align-middle text-xs max-w-[160px] truncate" title={row.sube || undefined}>
                        {row.sube || '-'}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-right font-medium whitespace-nowrap">
                        {row.toplam_adet ?? '-'}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-right text-xs font-medium whitespace-nowrap">
                        {formatCurrency(row.estimated_total_cost)}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-center whitespace-nowrap">
                        {statusBadge(row)}
                      </td>
                      <td className="py-2.5 align-middle text-center whitespace-nowrap">
                        {parsedByBadge(row.parsed_by)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {count > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t">
                <span className="text-xs text-muted-foreground">
                  {rangeFrom}-{rangeTo} / {count} kayit · Sayfa {page + 1} / {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
